import { useEffect, useState } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Flame, Trophy, Star, Award, TrendingUp, Target } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export default function GamificationCard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [progress, setProgress] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [checkingIn, setCheckingIn] = useState(false);

  useEffect(() => {
    fetchProgress();
    fetchAchievements();
  }, []);

  const fetchProgress = async () => {
    try {
      const response = await axios.get(`${API}/gamification/my-progress`, { withCredentials: true });
      setProgress(response.data);
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  const fetchAchievements = async () => {
    try {
      const response = await axios.get(`${API}/gamification/achievements`);
      setAchievements(response.data);
    } catch (error) {
      console.error('Error fetching achievements:', error);
    }
  };

  const handleCheckIn = async () => {
    setCheckingIn(true);
    try {
      const response = await axios.post(`${API}/gamification/check-in`, {}, { withCredentials: true });
      
      if (response.data.already_checked_in) {
        toast.info(t('gamification.alreadyCheckedIn'));
      } else {
        if (response.data.new_achievements.length > 0) {
          toast.success(`🎉 ${t('gamification.checkInSuccess')} + New Achievement!`);
        } else {
          toast.success(`🔥 ${t('gamification.checkInSuccess')} ${response.data.current_streak} ${t('gamification.dayStreak')}`);
        }
        fetchProgress();
      }
    } catch (error) {
      console.error('Error checking in:', error);
      toast.error(t('errors.generic'));
    } finally {
      setCheckingIn(false);
    }
  };

  if (!progress) return null;

  const unlockedAchievements = achievements.filter(a => progress.achievements_unlocked.includes(a.id));
  const lockedAchievements = achievements.filter(a => !progress.achievements_unlocked.includes(a.id));

  return (
    <Card className="p-6 bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 border-2 border-orange-200">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-orange-400 to-red-500 rounded-full">
            <Trophy className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">{t('gamification.title')}</h3>
            <p className="text-sm text-orange-600">Level {progress.level} • {progress.experience_points} XP</p>
          </div>
        </div>
        <Button
          onClick={handleCheckIn}
          disabled={checkingIn}
          size="sm"
          className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
        >
          <Flame className="h-4 w-4" />
          {t('gamification.checkIn')}
        </Button>
      </div>

      {/* Streak */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-white p-4 rounded-lg border-2 border-orange-200 text-center">
          <Flame className="h-8 w-8 mx-auto mb-2 text-orange-500" />
          <p className="text-3xl font-bold text-orange-600">{progress.current_streak}</p>
          <p className="text-xs text-gray-600">{t('gamification.currentStreak')}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border-2 border-orange-200 text-center">
          <Star className="h-8 w-8 mx-auto mb-2 text-yellow-500" />
          <p className="text-3xl font-bold text-yellow-600">{progress.longest_streak}</p>
          <p className="text-xs text-gray-600">{t('gamification.longestStreak')}</p>
        </div>
      </div>

      {/* Level Progress */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-gray-600 dark:text-gray-400">Level {progress.level}</span>
          <span className="font-medium text-orange-600 dark:text-orange-400">
            {progress.experience_points % 100}/{100} XP
          </span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
          <div
            className="bg-gradient-to-r from-orange-500 to-red-500 h-full rounded-full transition-all"
            style={{ 
              width: `${Math.max(((progress.experience_points % 100) / 100) * 100, 5)}%`
            }}
          />
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {progress.xp_to_next_level} XP to Level {progress.level + 1}
        </p>
      </div>

      {/* Achievements Preview */}
      <div>
        <h4 className="text-sm font-bold text-gray-900 mb-3">
          {t('gamification.achievements')} ({progress.achievements_unlocked.length}/{progress.total_achievements})
        </h4>
        <div className="flex flex-wrap gap-2">
          {unlockedAchievements.slice(0, 6).map((achievement) => (
            <Badge
              key={achievement.id}
              className="bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-700 border-orange-300 text-lg"
              title={achievement.description}
            >
              {achievement.icon}
            </Badge>
          ))}
          {lockedAchievements.slice(0, 3).map((achievement) => (
            <Badge
              key={achievement.id}
              variant="outline"
              className="bg-gray-50 text-gray-400 opacity-50 text-lg"
              title={`🔒 ${achievement.description}`}
            >
              {achievement.icon}
            </Badge>
          ))}
        </div>
      </div>
    </Card>
  );
}
